import {
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

// Backstage exposes app-config to the frontend via two paths:
//   1. process.env.APP_CONFIG - inlined at build time from the static
//      app-config.yaml + app-config.production.yaml files.
//   2. <script type="backstage.io/config"> tags in index.html - populated
//      at request time by the backend, including all APP_CONFIG_* env-var
//      overrides delivered via the faro-config K8s Secret.
//
// We need (2) because the Faro URL is delivered as a runtime override; (1)
// would only ever see the empty placeholder. We read both so local dev that
// configures the URL in app-config.local.yaml also works.
function readFromScriptTags<T>(
  pick: (data: { [key: string]: unknown }) => T | undefined,
): T | undefined {
  if (typeof document === 'undefined') return undefined;
  const tags = document.querySelectorAll<HTMLScriptElement>(
    'script[type="backstage.io/config"]',
  );
  for (const tag of Array.from(tags)) {
    if (!tag.textContent) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(tag.textContent);
    } catch {
      continue;
    }
    if (!Array.isArray(parsed)) continue;
    for (const entry of parsed) {
      const data = (entry as { data?: { [key: string]: unknown } })?.data;
      if (!data) continue;
      const value = pick(data);
      if (value !== undefined) return value;
    }
  }
  return undefined;
}

function readFromBuildEnv<T>(
  pick: (data: { [key: string]: unknown }) => T | undefined,
): T | undefined {
  const cfg = (process.env as { APP_CONFIG?: unknown }).APP_CONFIG;
  if (!Array.isArray(cfg)) return undefined;
  for (const entry of cfg) {
    const data = (entry as { data?: { [key: string]: unknown } })?.data;
    if (!data) continue;
    const value = pick(data);
    if (value !== undefined) return value;
  }
  return undefined;
}

function readConfig<T>(
  pick: (data: { [key: string]: unknown }) => T | undefined,
): T | undefined {
  return readFromScriptTags(pick) ?? readFromBuildEnv(pick);
}

function readFaroUrl(): string | undefined {
  return readConfig<string>(data => {
    const url = (
      data as { app?: { observability?: { faro?: { url?: unknown } } } }
    )?.app?.observability?.faro?.url;
    return typeof url === 'string' && url.length > 0 ? url : undefined;
  });
}

function readAppVersion(): string | undefined {
  return readConfig<string>(data => {
    const v = (data as { app?: { version?: unknown } })?.app?.version;
    return typeof v === 'string' && v.length > 0 ? v : undefined;
  });
}

export function initFaro(): void {
  const url = readFaroUrl();
  if (!url) {
    // eslint-disable-next-line no-console
    console.log('Faro disabled - no collector URL configured');
    return;
  }
  // The Grafana Cloud Frontend Observability collector URL embeds the app
  // token in its path, so no separate apiKey is required.
  try {
    initializeFaro({
      url,
      app: {
        name: 'backstage',
        version: readAppVersion() ?? 'unknown',
        environment: 'production',
      },
      instrumentations: [
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
      ],
    });
    // eslint-disable-next-line no-console
    console.log('Faro initialized successfully');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to initialize Faro:', error);
  }
}
